// Shared launch timeclock contract for the record_time_punch RPC.
// The authenticated caller/user comes from Supabase auth; this payload records only
// the account-scoped punch facts the client is allowed to submit for itself.

import { findSecretShape } from "./field-evidence.ts";

export const TIME_PUNCH_TYPES = ["clock_in", "clock_out", "break_start", "break_end"] as const;
export type TimePunchType = (typeof TIME_PUNCH_TYPES)[number];

export const TIME_PUNCH_GPS_PERMISSION_STATES = ["granted", "denied", "unavailable", "unknown"] as const;
export type TimePunchGpsPermissionState = (typeof TIME_PUNCH_GPS_PERMISSION_STATES)[number];

export interface RecordTimePunchPayloadInput {
  readonly accountId?: string | null;
  readonly punchType?: string | null;
  readonly deviceTime?: string | null;
  readonly deviceId?: string | null;
  readonly appVersion?: string | null;
  readonly routeAssignmentId?: string | null;
  readonly lat?: number | null;
  readonly lng?: number | null;
  readonly gpsAccuracyMeters?: number | null;
  readonly gpsPermission?: string | null;
  readonly offlineCreated?: boolean | null;
  readonly notes?: string | null;
  readonly metadata?: unknown;
}

export interface RecordTimePunchPayload {
  readonly accountId: string;
  readonly punchType: TimePunchType;
  readonly deviceTime: string;
  readonly deviceId: string;
  readonly appVersion: string;
  readonly routeAssignmentId: string | null;
  readonly lat: number | null;
  readonly lng: number | null;
  readonly gpsAccuracyMeters: number | null;
  readonly gpsPermission: TimePunchGpsPermissionState;
  readonly offlineCreated: boolean;
  readonly notes: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
}

export type RecordTimePunchClosedReason =
  | "account_id_required"
  | "punch_type_required"
  | "punch_type_unsupported"
  | "device_time_required"
  | "device_id_required"
  | "app_version_required"
  | "route_assignment_id_invalid"
  | "gps_coordinates_required_together"
  | "gps_coordinates_invalid"
  | "gps_coordinates_out_of_range"
  | "gps_accuracy_invalid"
  | "gps_accuracy_negative"
  | "gps_permission_unsupported"
  | "offline_created_invalid"
  | "notes_invalid"
  | "notes_contains_restricted_value"
  | "metadata_contains_restricted_value"
  | "metadata_not_plain_object";

export interface RecordTimePunchNormalized {
  readonly ok: true;
  readonly payload: RecordTimePunchPayload;
}

export interface RecordTimePunchClosed {
  readonly ok: false;
  readonly reason: RecordTimePunchClosedReason;
  readonly message: string;
}

export type RecordTimePunchNormalizationResult = RecordTimePunchNormalized | RecordTimePunchClosed;

export function isTimePunchType(value: string): value is TimePunchType {
  return (TIME_PUNCH_TYPES as readonly string[]).includes(value);
}

export function normalizeRecordTimePunchPayload(input: RecordTimePunchPayloadInput): RecordTimePunchNormalizationResult {
  const accountId = requiredText(input.accountId, "account_id_required", "Account is required to record a time punch.");
  if (!accountId.ok) {
    return accountId;
  }

  const punchTypeText = requiredText(
    input.punchType,
    "punch_type_required",
    "Time punch type is required to record a time punch.",
  );
  if (!punchTypeText.ok) {
    return punchTypeText;
  }
  if (!isTimePunchType(punchTypeText.value)) {
    return closed("punch_type_unsupported", "Time punch type is not supported.");
  }

  const deviceTime = requiredText(
    input.deviceTime,
    "device_time_required",
    "Device time is required to record a time punch.",
  );
  if (!deviceTime.ok) {
    return deviceTime;
  }

  const deviceId = requiredText(input.deviceId, "device_id_required", "Device id is required to record a time punch.");
  if (!deviceId.ok) {
    return deviceId;
  }

  const appVersion = requiredText(
    input.appVersion,
    "app_version_required",
    "App version is required to record a time punch.",
  );
  if (!appVersion.ok) {
    return appVersion;
  }

  const routeAssignmentId = optionalText(input.routeAssignmentId);
  if (routeAssignmentId === undefined) {
    return closed("route_assignment_id_invalid", "Route assignment id must be a string when provided.");
  }

  const coordinates = normalizeCoordinates(input.lat, input.lng);
  if (!coordinates.ok) {
    return coordinates;
  }

  const gpsAccuracyMeters = normalizeGpsAccuracy(input.gpsAccuracyMeters);
  if (!gpsAccuracyMeters.ok) {
    return gpsAccuracyMeters;
  }

  const gpsPermission = normalizeGpsPermission(input.gpsPermission);
  if (!gpsPermission.ok) {
    return gpsPermission;
  }

  const offlineCreated = normalizeOfflineCreated(input.offlineCreated);
  if (!offlineCreated.ok) {
    return offlineCreated;
  }

  const notes = normalizeNotes(input.notes);
  if (!notes.ok) {
    return notes;
  }

  const metadata = normalizeMetadata(input.metadata);
  if (!metadata.ok) {
    return metadata;
  }

  return {
    ok: true,
    payload: {
      accountId: accountId.value,
      appVersion: appVersion.value,
      deviceId: deviceId.value,
      deviceTime: deviceTime.value,
      gpsAccuracyMeters: gpsAccuracyMeters.value,
      gpsPermission: gpsPermission.value,
      lat: coordinates.lat,
      lng: coordinates.lng,
      metadata: metadata.value,
      notes: notes.value,
      offlineCreated: offlineCreated.value,
      punchType: punchTypeText.value,
      routeAssignmentId,
    },
  };
}

function requiredText(
  value: string | null | undefined,
  reason: RecordTimePunchClosedReason,
  message: string,
): { readonly ok: true; readonly value: string } | RecordTimePunchClosed {
  if (typeof value !== "string") {
    return closed(reason, message);
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return closed(reason, message);
  }
  return { ok: true, value: trimmed };
}

function optionalText(value: string | null | undefined): string | null | undefined {
  if (value == null) {
    return null;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function normalizeCoordinates(
  lat: number | null | undefined,
  lng: number | null | undefined,
): { readonly ok: true; readonly lat: number | null; readonly lng: number | null } | RecordTimePunchClosed {
  const hasLat = lat != null;
  const hasLng = lng != null;
  if (hasLat !== hasLng) {
    return closed("gps_coordinates_required_together", "GPS latitude and longitude must be provided together.");
  }
  if (!hasLat && !hasLng) {
    return { ok: true, lat: null, lng: null };
  }
  if (typeof lat !== "number" || typeof lng !== "number" || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return closed("gps_coordinates_invalid", "GPS coordinates must be finite numbers.");
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return closed("gps_coordinates_out_of_range", "GPS coordinates are outside allowed ranges.");
  }
  return { ok: true, lat, lng };
}

function normalizeGpsAccuracy(
  value: number | null | undefined,
): { readonly ok: true; readonly value: number | null } | RecordTimePunchClosed {
  if (value == null) {
    return { ok: true, value: null };
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return closed("gps_accuracy_invalid", "GPS accuracy must be a finite number.");
  }
  if (value < 0) {
    return closed("gps_accuracy_negative", "GPS accuracy cannot be negative.");
  }
  return { ok: true, value };
}

function normalizeGpsPermission(
  value: string | null | undefined,
): { readonly ok: true; readonly value: TimePunchGpsPermissionState } | RecordTimePunchClosed {
  if (value == null) {
    return { ok: true, value: "unknown" };
  }
  const trimmed = value.trim();
  if ((TIME_PUNCH_GPS_PERMISSION_STATES as readonly string[]).includes(trimmed)) {
    return { ok: true, value: trimmed as TimePunchGpsPermissionState };
  }
  return closed("gps_permission_unsupported", "GPS permission is not supported.");
}

function normalizeOfflineCreated(
  value: boolean | null | undefined,
): { readonly ok: true; readonly value: boolean } | RecordTimePunchClosed {
  if (value == null) {
    return { ok: true, value: false };
  }
  if (typeof value !== "boolean") {
    return closed("offline_created_invalid", "Offline created must be a boolean.");
  }
  return { ok: true, value };
}

function normalizeNotes(
  value: string | null | undefined,
): { readonly ok: true; readonly value: string | null } | RecordTimePunchClosed {
  if (value == null) {
    return { ok: true, value: null };
  }
  if (typeof value !== "string") {
    return closed("notes_invalid", "Time punch notes must be a string when provided.");
  }
  const trimmed = value.trim();
  if (findSecretShape(trimmed)) {
    return closed("notes_contains_restricted_value", "Time punch notes contain a restricted value.");
  }
  return { ok: true, value: trimmed.length === 0 ? null : trimmed };
}

function normalizeMetadata(
  value: unknown,
): { readonly ok: true; readonly value: Readonly<Record<string, unknown>> } | RecordTimePunchClosed {
  if (value == null) {
    return { ok: true, value: {} };
  }
  if (!isPlainObject(value)) {
    return closed("metadata_not_plain_object", "Time punch metadata must be a plain object.");
  }
  let serialized: string;
  try {
    serialized = JSON.stringify(value) ?? "";
  } catch {
    return closed("metadata_not_plain_object", "Time punch metadata must be a JSON-compatible plain object.");
  }
  if (findSecretShape(serialized)) {
    return closed("metadata_contains_restricted_value", "Time punch metadata contains a restricted value.");
  }
  return { ok: true, value: { ...value } };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function closed(reason: RecordTimePunchClosedReason, message: string): RecordTimePunchClosed {
  return { ok: false, reason, message };
}
